/**
 * SVG Icon Preloader Service
 * Preloads all SVG icons used throughout the application to prevent "pop-in" effects
 */

class SvgPreloader {
  constructor() {
    this.preloadedIcons = new Set();
    this.preloadPromises = new Map();
  }

  /**
   * List of all SVG icons used in the application
   */
  static getAllIcons() {
    return [
      // Step3 - Household icons
      "/assets/icons/BATCON - 1 person.svg",
      "/assets/icons/BATCON - 2 person.svg", 
      "/assets/icons/BATCON - 3-4 person.svg",
      "/assets/icons/BATCON - 5+ person.svg",
      
      // Step4 - Location icons
      "/assets/icons/BATCON - Garage.svg",
      "/assets/icons/BATCON - kelder.svg",
      "/assets/icons/BATCON - Zolder.svg",
      "/assets/icons/BATCON - Weet ik niet.svg",
      
      // Step5 - Priority icons
      "/assets/icons/BATCON - Besparing.svg",
      "/assets/icons/BATCON - Optimaliseren eigen gebruik.svg",
      "/assets/icons/BATCON - Zekerheid stroomuitval.svg",
      
      // Step7 - Process icons
      "/assets/icons/BATCON - Advies.svg",
      "/assets/icons/BATCON - Offerte.svg",
      "/assets/icons/BATCON - Monteren.svg",
      "/assets/icons/BATCON - Oplevering.svg",
      "/assets/icons/BATCON - Geniet.svg"
    ];
  }

  /**
   * Preload a single icon
   * @param {string} iconPath - Path to the SVG icon
   * @returns {Promise} - Promise that resolves when icon is loaded
   */
  preloadIcon(iconPath) {
    if (this.preloadedIcons.has(iconPath)) {
      return Promise.resolve();
    }

    if (this.preloadPromises.has(iconPath)) {
      return this.preloadPromises.get(iconPath);
    }

    const promise = new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.preloadedIcons.add(iconPath);
        this.preloadPromises.delete(iconPath);
        resolve();
      };
      
      img.onerror = () => {
        console.warn(`Failed to preload SVG icon: ${iconPath}`);
        this.preloadPromises.delete(iconPath);
        reject(new Error(`Failed to load ${iconPath}`));
      };
      
      img.src = iconPath;
    });

    this.preloadPromises.set(iconPath, promise);
    return promise;
  }

  /**
   * Preload all SVG icons used in the application
   * @returns {Promise} - Promise that resolves when all icons are loaded
   */
  preloadAllIcons() {
    const allIcons = SvgPreloader.getAllIcons();
    const preloadPromises = allIcons.map(icon => 
      this.preloadIcon(icon).catch(err => {
        console.warn(`SVG preload warning:`, err);
        return null; // Don't let one failed icon break the whole process
      })
    );

    return Promise.allSettled(preloadPromises);
  }

  /**
   * Preload icons for a specific step
   * @param {number} stepNumber - Step number (3, 4, 5, 7)
   * @returns {Promise} - Promise that resolves when step icons are loaded
   */
  preloadStepIcons(stepNumber) {
    const stepIconMap = {
      3: [
        "/assets/icons/BATCON - 1 person.svg",
        "/assets/icons/BATCON - 2 person.svg", 
        "/assets/icons/BATCON - 3-4 person.svg",
        "/assets/icons/BATCON - 5+ person.svg"
      ],
      4: [
        "/assets/icons/BATCON - Garage.svg",
        "/assets/icons/BATCON - kelder.svg",
        "/assets/icons/BATCON - Zolder.svg",
        "/assets/icons/BATCON - Weet ik niet.svg"
      ],
      5: [
        "/assets/icons/BATCON - Besparing.svg",
        "/assets/icons/BATCON - Optimaliseren eigen gebruik.svg",
        "/assets/icons/BATCON - Zekerheid stroomuitval.svg"
      ],
      7: [
        "/assets/icons/BATCON - Advies.svg",
        "/assets/icons/BATCON - Offerte.svg",
        "/assets/icons/BATCON - Monteren.svg",
        "/assets/icons/BATCON - Oplevering.svg"
      ]
    };

    const icons = stepIconMap[stepNumber] || [];
    const preloadPromises = icons.map(icon => this.preloadIcon(icon));
    
    return Promise.allSettled(preloadPromises);
  }

  /**
   * Check if an icon is already preloaded
   * @param {string} iconPath - Path to the SVG icon
   * @returns {boolean} - True if icon is preloaded
   */
  isIconPreloaded(iconPath) {
    return this.preloadedIcons.has(iconPath);
  }

  /**
   * Get preload status
   * @returns {Object} - Status object with counts
   */
  getStatus() {
    const totalIcons = SvgPreloader.getAllIcons().length;
    const loadedCount = this.preloadedIcons.size;
    const loadingCount = this.preloadPromises.size;
    
    return {
      total: totalIcons,
      loaded: loadedCount,
      loading: loadingCount,
      remaining: totalIcons - loadedCount,
      progress: Math.round((loadedCount / totalIcons) * 100)
    };
  }
}

// Export singleton instance
export default new SvgPreloader();